/**
 * Minimal signature-drawing canvas. Pointer-based (works on mouse +
 * touch + stylus). No dependencies.
 *
 * Shipped for the Toka waiver block (2026-10-12) to replace the typed-
 * name signature. A drawn signature is legally stronger than typing a
 * name into a text box, courts routinely recognise mouse/touch
 * signatures (and most e-signature platforms use the same primitive).
 *
 * Output: `toDataURL()` → base64 PNG. We send that as the `signature`
 * string to the existing POST /api/venue/:venueId/waiver, which already
 * accepts a string and snapshots it verbatim onto the WaiverSignature
 * row. Zero schema change, drop-in replacement for the typed field.
 *
 * The parent decides whether a drawn signature is present via the
 * `onChange` callback, which fires on pointer-up with the latest data
 * URL (or `null` when the user clears).
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

export function SignaturePad({
  onChange,
  heightPx = 180,
}: {
  onChange: (dataUrl: string | null) => void;
  heightPx?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  // Match the backing canvas pixel buffer to the CSS size on every
  // mount + viewport resize. Without this, mobile Safari paints at the
  // device-pixel ratio and the stroke appears thinner than the finger
  // tip (plus coordinates mis-align).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2;
      // High-contrast ink against the ivory signature line.
      ctx.strokeStyle = "#111111";
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  const localPoint = (e: ReactPointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    // Capture the pointer so we keep receiving move events if the user
    // drags off the canvas (common with large signatures).
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastRef.current = localPoint(e);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || !lastRef.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const next = localPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
    lastRef.current = next;
    if (!hasInk) setHasInk(true);
  };

  const commit = () => {
    drawingRef.current = false;
    lastRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Report the latest signature on every pointer-up so the parent
    // keeps an up-to-date dataURL ready for submit.
    if (hasInk) onChange(canvas.toDataURL("image/png"));
    else onChange(null);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;
    // Reset the backing buffer; the DPR-scale is re-applied by the
    // resize effect's next tick on `clearRect`-only draw, we don't
    // need to re-init here because we only cleared pixels.
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <div
        className="relative overflow-hidden rounded-md border-2"
        style={{
          borderColor: "var(--accent)",
          background: "#f5f1e8",
          height: `${heightPx}px`,
        }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={commit}
          onPointerCancel={commit}
          style={{ width: "100%", height: "100%", touchAction: "none", cursor: "crosshair" }}
        />
        {/* Signature baseline + placeholder, soft like a paper form. */}
        {!hasInk && (
          <div
            className="pointer-events-none absolute inset-x-5 bottom-5 flex items-end justify-start"
            style={{ color: "#9a8f72" }}
          >
            <span className="text-xs italic">Sign above the line</span>
          </div>
        )}
        <div
          className="pointer-events-none absolute inset-x-5 bottom-10 h-px"
          style={{ background: "#9a8f72" }}
        />
      </div>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={clear}
          disabled={!hasInk}
          className="rounded border-0 bg-transparent p-0 text-[11px] font-semibold uppercase tracking-wider underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
          style={{ color: "var(--venue-text)" }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
